import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { api, type PodcastVideoRecord } from '../../services/api';
import {
  PodcastVideoCard,
  PodcastVideoPlayerDialog,
  podcastRecordToVideoItem,
} from './PodcastVideosTab';

// ---------------------------------------------------------------------------
// ProjectPodcastVideosDialog
//
// A mini, read-only version of the Podcast Videos tab scoped to a single
// project. Surfaced from the project Details tab (alongside the EDMs /
// eBooks quick-list buttons) so users can browse the verified podcast
// clips tagged to that specific project without leaving the detail view.
//
// Only the search field is carried over from the full tab — there is no
// "Add Video" action, no project filter (it's already project-scoped),
// and no analytics. Cards reuse the same `PodcastVideoCard` component as
// the public feed, and playback reuses the shared `PodcastVideoPlayerDialog`.
// ---------------------------------------------------------------------------

interface ProjectPodcastVideosDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string | null | undefined;
  projectName?: string | null;
  // Optional preloaded verified videos tagged to this project. When
  // provided, the dialog skips its own fetch and renders these
  // directly (the parent already loads them to drive the button
  // count). When omitted, the dialog self-fetches so it stays
  // reusable in isolation.
  videos?: PodcastVideoRecord[] | null;
}

export default function ProjectPodcastVideosDialog({
  open,
  onClose,
  projectId,
  projectName,
  videos,
}: ProjectPodcastVideosDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const usePreloaded = videos != null;

  const [fetchedRecords, setFetchedRecords] = useState<PodcastVideoRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeRecord, setActiveRecord] = useState<PodcastVideoRecord | null>(null);

  const records = usePreloaded ? (videos as PodcastVideoRecord[]) : fetchedRecords;

  // Reset the search box each time the dialog opens.
  useEffect(() => {
    if (open) setSearchQuery('');
  }, [open]);

  // Fetch the verified feed whenever the dialog opens for a project,
  // then narrow to rows tagged to this project. `listPodcastVideos`
  // already returns verified-only rows with their project tags, so the
  // filtering can happen entirely client-side. Skipped entirely when
  // the parent supplies a preloaded `videos` list.
  useEffect(() => {
    if (usePreloaded) return;
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listPodcastVideos()
      .then((rows) => {
        if (cancelled) return;
        setFetchedRecords(
          rows.filter((r) => (r.projects ?? []).some((p) => p.id === projectId)),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load podcast videos',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, usePreloaded]);

  const filteredRecords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => {
      const haystack = [
        r.title ?? '',
        r.videoUrl,
        r.createdByName ?? '',
        r.createdBy,
        ...(r.projects ?? []).map((p) => p.name),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [records, searchQuery]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text)',
            borderRadius: isMobile ? 0 : 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
            >
              Podcast Videos
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              Verified clips tagged to {projectName || 'this project'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: 'var(--color-bg)' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by title or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'var(--color-surface-elevated)',
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.12)' },
                '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
              },
              '& .MuiInputBase-input': {
                color: 'var(--color-text)',
                fontWeight: 500,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--color-text-muted)', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ color: 'var(--color-text-muted)' }}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography
              variant="body2"
              sx={{ color: 'error.main', py: 6, textAlign: 'center' }}
            >
              {error}
            </Typography>
          ) : filteredRecords.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: 'var(--color-text-strong)', fontWeight: 600 }}
              >
                No podcast videos found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'var(--color-text-muted)',
                  maxWidth: 400,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                {records.length === 0
                  ? 'There are no verified podcast videos tagged to this project yet.'
                  : 'Try adjusting your search query.'}
              </Typography>
            </Stack>
          ) : (
            <Box
              sx={{
                columnCount: { xs: 2, sm: 3, md: 4 },
                columnGap: { xs: 2, sm: 2, md: 2 },
                p: 0.5,
                '& > *': {
                  breakInside: 'avoid',
                  WebkitColumnBreakInside: 'avoid',
                  pageBreakInside: 'avoid',
                  mb: { xs: 1.5, sm: 2, md: 2.5 },
                  display: 'block',
                },
              }}
            >
              {filteredRecords.map((record) => (
                <PodcastVideoCard
                  key={record.id}
                  video={podcastRecordToVideoItem(record)}
                  onPlay={() => setActiveRecord(record)}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <PodcastVideoPlayerDialog
        record={activeRecord}
        onClose={() => setActiveRecord(null)}
      />
    </>
  );
}
